import { z } from "zod";

/**
 * Tenant wire schemas — Unit #18.
 *
 * The full row shape (Tenant) lives in reference.ts and was extended there
 * to expose all 6 columns (Unit #18 added `erReturnMinutes` and
 * `defaultDoBillableCents` to the response). This file owns the PATCH input
 * and the PUBLIC, no-auth phone body.
 *
 * `PatchTenant` is the OFFICE-facing PATCH body — every field optional +
 * nullable (clearing is a valid update for `phone` and `photoRetentionMonths`).
 * Field bounds (locked during Unit #18 architecture):
 *   - phone                : `^(?=.*\d)[\d\s\-()+]{7,20}$` or null (cleared)
 *   - photoRetentionMonths : integer 0–600 or null (null = purge disabled)
 *   - erReturnMinutes      : integer 1–1440 (1 min to 24 hr)
 *   - defaultDoBillableCents: integer 0–99_999_999 (magnitude discipline —
 *     same bound discipline Unit #17 introduced for review-money forms).
 *
 * `TenantPhone` is the PUBLIC, no-auth body for the driver app's pre-auth
 * "Call dispatch" and "Forgot PIN? Call office" surfaces. Only the phone is
 * exposed — never the rest of the row.
 *
 * `.strict()` is intentionally NOT used on PatchTenant: clients that send a
 * future field get a 400, which is wrong for a forward-compatible PATCH. The
 * route applies a per-key allowlist (only the 4 fields above) so unknown keys
 * are dropped before they reach SQL.
 */

const PHONE_RE = /^(?=.*\d)[\d\s\-()+]{7,20}$/;

export const PatchTenant = z
  .object({
    phone: z
      .string()
      .regex(PHONE_RE, "Phone must be 7-20 chars: digits, spaces, dashes, parens, or +")
      .nullable(),
    // DISPATCH-PHONE (PM 2026-09-17): fallback #2 for the driver "Call
    // Dispatch" button (used only when no active-session dispatcher has a
    // phone). Same shape discipline as `phone`: the office may clear it to
    // null; .partial() above makes it optional on the PATCH body.
    dispatcherDefaultPhone: z
      .string()
      .regex(PHONE_RE, "Phone must be 7-20 chars: digits, spaces, dashes, parens, or +")
      .nullable(),
    photoRetentionMonths: z
      .number()
      .int()
      .min(0, "Retention must be ≥ 0 months")
      .max(600, "Retention must be ≤ 600 months (50 years)")
      .nullable(),
    erReturnMinutes: z
      .number()
      .int()
      .min(1, "E/R return minutes must be ≥ 1")
      .max(1440, "E/R return minutes must be ≤ 1440 (24 hours)"),
    defaultDoBillableCents: z
      .number()
      .int()
      .min(0, "D/O default billable must be ≥ 0")
      .max(99_999_999, "D/O default billable must be ≤ 99,999,999 cents"),
    timeclockNfcRequired: z.boolean(),
    officeKeyRequired: z.boolean(),
    // SPEC-68 §3.1: the passkey policy flip — audited on the route.
    passkeysRequired: z.boolean(),
    // SPEC-67 §3.7: time-boxed binding disable (break-glass #2). number = set
    // the window (SQL-side now() + interval); null = re-enable now. Cap 24 h
    // is enforced HERE so "permanently off by accident" is unreachable.
    deviceBindingDisabledHours: z
      .number()
      .int()
      .min(1, "Device-binding disable must be ≥ 1 hour")
      .max(24, "Device-binding disable must be ≤ 24 hours")
      .nullable(),
  })
  .partial();
export type PatchTenant = z.infer<typeof PatchTenant>;

/** Public, no-auth phone response — driver app pre-auth screens. */
export const TenantPhone = z.object({
  phone: z.string().nullable(),
});
export type TenantPhone = z.infer<typeof TenantPhone>;

/** Preview response for the photo-purge card count. */
export const PurgePreview = z.object({
  purgeable: z.number().int(),
});
export type PurgePreview = z.infer<typeof PurgePreview>;

/**
 * Purge response — `purged` = DB row deletes that succeeded; `failed` = R2
 * delete failures (the orphan R2 object is logged but the row+DB clean-up
 * still happened, so `purged` may be > 0 while `failed` is also > 0).
 * `orphanCleanup: true` is set when the caller used `orphansOnly: true` —
 * R2-only cleanup, no DB row work.
 */
export const PurgeResult = z.object({
  purged: z.number().int(),
  failed: z.number().int(),
  orphanCleanup: z.boolean().optional(),
});
export type PurgeResult = z.infer<typeof PurgeResult>;
